import {Component, linkedSignal, OnInit, signal, ViewChild, inject, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {StoreDefaultWorkingTime} from '../../../../classes/models/store/StoreDefaultWorkingTime';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {DayOfWeek, dayOfWeekValues} from '../../../../classes/enums/DayOfWeek';
import {StoreDefaultWorkingTimeService} from '../../../../services/http/store/store-default-working-time.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";
import {
  WorkingTimesFormTimepickerComponent
} from "./working-times-form-timepicker/working-times-form-timepicker.component";

/**
 * Defines the data required by the working times form modal.
 */
export interface DefaultWorkingTimesFormData {
  storeId?: string;
  previousDefaultWorkingTime?: StoreDefaultWorkingTime;
  weekExists: boolean;
}

/**
 * This component handles creating and editing working times.
 */
@Component({
  selector: 'app-default-working-times-form',
  templateUrl: './default-working-times-form.component.html',
  styleUrls: ['./default-working-times-form.component.less'],
  imports: [SHARED_IMPORTS, WorkingTimesFormTimepickerComponent]
})
export class DefaultWorkingTimesFormComponent implements OnInit {
  private storeDefaultWorkingTimeService = inject(StoreDefaultWorkingTimeService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private dataService = inject(DataService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** A modal of working time form. */
  @ViewChild('modalComponent') modalComponent: ModalComponent;
  /** An identifier of a store. */
  storeId = signal<string>(undefined);
  /** Previous default store working times. */
  previousDefaultWorkingTime = signal<StoreDefaultWorkingTime | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  weekExists = signal<boolean>(false);

  /** Form control value of the Monday of the week, from which default working times will take effect. */
  startDate = linkedSignal(() =>
          this.weekExists()
                  ? this.previousDefaultWorkingTime()?.startDate
                  : undefined
  );
  /** Form control values of store working time for each day of week. */
  weeklyTimes = linkedSignal(() => {
    const prev = this.previousDefaultWorkingTime();
    if (prev) {
      return prev.weeklyTimes;
    }
    const weeklyTimes = new Map<DayOfWeek, StoreWorkingTime>();
    dayOfWeekValues().forEach(dayOfWeek =>
            weeklyTimes.set(dayOfWeek, new StoreWorkingTime('', ''))
    );
    return weeklyTimes;
  })
  /** `True` if request to backend is in progress. */
  loading = signal(false);
  /** The first date from which data can be edited. */
  editDate: string;
  /** Assigns days of week values to be used in HTML. */
  daysOfWeekValues: DayOfWeek[] = dayOfWeekValues();

  static open(modalService: NgbModal, data: DefaultWorkingTimesFormData): NgbModalRef {
    const modalRef = modalService.open(DefaultWorkingTimesFormComponent);
    modalRef.componentInstance.storeId.set(data.storeId);
    modalRef.componentInstance.previousDefaultWorkingTime.set(data.previousDefaultWorkingTime);
    modalRef.componentInstance.weekExists.set(data.weekExists);
    return modalRef;
  }

  ngOnInit(): void {
    this.editDate = this.dataService.editDate;
  }

  /**
   * Sends request to backend to create or update working times.
   */
  onSubmit(): void {
    this.loading.set(true);
    const defaultWorkingTimes = new StoreDefaultWorkingTime(this.startDate(), this.weeklyTimes());

    if (this.weekExists()) {
      this.editDefaultWorkingTime(defaultWorkingTimes);
    } else {
      this.addDefaultWorkingTime(defaultWorkingTimes);
    }
  }

  addDefaultWorkingTime(defaultWorkingTimes: StoreDefaultWorkingTime): void {
    this.storeDefaultWorkingTimeService.addDefaultStoreWorkingTime(this.storeId(), defaultWorkingTimes)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store trading time successfully created');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  editDefaultWorkingTime(defaultWorkingTimes: StoreDefaultWorkingTime): void {
    this.storeDefaultWorkingTimeService.editDefaultStoreWorkingTime(this.storeId(), defaultWorkingTimes)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store trading time successfully updated');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}